import { DiscordDelivery } from './DiscordDelivery';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useEvents, useProject, useRecordEvent } from '../api/queries';
import type { EventPayload, OperationalEvent, Project, ProjectStage, Visibility } from '../api/types';
import { Field } from '../components/Form';
import { EmptyState, ErrorState, Loading } from '../components/States';
import { buildPayload, localToIso, stages } from '../lib/events';
import { formatDateTime, isSafeExternalUrl } from '../lib/format';
import { PILOTS } from './Pilots';

const stageLabel = (stage: ProjectStage | undefined) => {
  if (stage === undefined || stage === 'unknown') return 'Stage not known';
  if (stage === 'shipped') return 'Reported stage: shipped (reported, not verified deployment)';
  return `Reported stage: ${stage}`;
};

const audienceLabel = (visibility: Visibility) =>
  visibility === 'members' ? 'invited members' : visibility === 'stewards' ? 'stewards' : 'the owner only';

const nowLocal = () => new Date().toISOString().slice(0, 16);

/** Recorded updates from the authorized event history; nothing is inferred or invented. */
function History({ project }: { project: Project }) {
  const events = useEvents({ project: project.id, type: 'project_update' });
  if (events.isPending) return <Loading label="Loading recorded updates" />;
  if (events.isError) return <ErrorState error={events.error} onRetry={() => void events.refetch()} />;
  const updates = [...events.data.items].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  if (updates.length === 0) {
    return (
      <EmptyState title="No recorded updates yet">
        <p>Updates appear here only after someone records one. Nothing is filled in from other activity.</p>
      </EmptyState>
    );
  }
  return (
    <>
      {events.data.nextCursor !== undefined && events.data.nextCursor !== null && (
        <p className="small muted">Showing the most recent {updates.length} recorded updates.</p>
      )}
      <ol className="history" aria-label={`Recorded updates for ${project.title}`}>
        {updates.map((update: OperationalEvent) => (
          <li key={update.id} className="history__item">
            <p className="history__head">
              <span className="history__version">{formatDateTime(update.occurredAt)}</span>
              <span className="tag tag--wrap">{stageLabel(update.payload.stage)}</span>
            </p>
            <p className="annotation__body">{update.payload.note ?? <span className="muted">Note withheld by attribution preference</span>}</p>
            {isSafeExternalUrl(update.payload.artifactUrl) && (
              <p className="small">
                <a href={update.payload.artifactUrl} target="_blank" rel="noreferrer noopener">
                  Referenced artifact<span className="visually-hidden"> (opens in a new tab)</span>
                </a>{' '}
                <span className="muted">— a link, not a pinned source version.</span>
              </p>
            )}
          </li>
        ))}
      </ol>
    </>
  );
}

/** Exactly what was sent when the outcome was unclear. It is resent unchanged or closed; never edited. */
interface Unresolved {
  occurredAt: string;
  visibility: Visibility;
  payload: EventPayload;
  failedAt: number;
  message: string;
}

type Refresh = 'refreshing' | 'refreshed' | 'failed';

/**
 * The owner drafts and previews an update locally; only an explicit record
 * action sends it. There is no idempotency key for these events, so an
 * unclear outcome freezes the submitted update until the owner reconciles it
 * against history that has actually been refreshed since the failure. Edit
 * and preview cannot clear it, and nothing is ever retried automatically.
 */
function Composer({ project }: { project: Project }) {
  const record = useRecordEvent();
  const events = useEvents({ project: project.id, type: 'project_update' });
  const [stage, setStage] = useState<ProjectStage>('unknown');
  const [note, setNote] = useState('');
  const [artifactUrl, setArtifactUrl] = useState('');
  const [occurredAt, setOccurredAt] = useState(nowLocal());
  const [visibility, setVisibility] = useState<Visibility>('members');
  const [previewing, setPreviewing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [recorded, setRecorded] = useState(false);
  const [refused, setRefused] = useState<{ code: string; message: string } | null>(null);
  const [unresolved, setUnresolved] = useState<Unresolved | null>(null);
  const [refresh, setRefresh] = useState<Refresh>('refreshing');
  const [checked, setChecked] = useState(false);

  function resetDraft() {
    setStage('unknown');
    setNote('');
    setArtifactUrl('');
    setOccurredAt(nowLocal());
    setPreviewing(false);
  }

  /** Only a successful fetch completed after the failure counts as a refresh. */
  async function refreshHistory(failedAt: number) {
    setRefresh('refreshing');
    setChecked(false);
    const result = await events.refetch();
    setRefresh(result.isSuccess && !result.isError && result.dataUpdatedAt >= failedAt ? 'refreshed' : 'failed');
  }

  function preview(event: FormEvent) {
    event.preventDefault();
    if (unresolved !== null) return;
    const built = buildPayload('project_update', { stage, note, artifactUrl });
    const next: Record<string, string> = { ...(built.errors as Record<string, string>) };
    if (note.trim() === '') next.note = 'Say what changed, was tried, or is needed.';
    if (localToIso(occurredAt) === null) next.occurredAt = 'Enter when this happened.';
    setErrors(next);
    if (Object.keys(next).length === 0) {
      setRecorded(false);
      setRefused(null);
      setPreviewing(true);
    }
  }

  function send(submission: { occurredAt: string; visibility: Visibility; payload: EventPayload }) {
    record.mutate(
      { type: 'project_update', projectId: project.id, ...submission },
      {
        onSuccess: () => {
          setRecorded(true);
          setRefused(null);
          setUnresolved(null);
          setChecked(false);
          resetDraft();
        },
        onError: (failure) => {
          if (failure instanceof ApiError && failure.status >= 400 && failure.status < 500) {
            // Definitive: nothing was recorded. The draft stays editable.
            setRefused({ code: failure.code, message: failure.message });
            setUnresolved(null);
            return;
          }
          const failedAt = Date.now();
          setUnresolved({ ...submission, failedAt, message: failure instanceof ApiError ? failure.message : 'No answer was received.' });
          void refreshHistory(failedAt);
        },
      },
    );
  }

  function recordDraft() {
    const built = buildPayload('project_update', { stage, note, artifactUrl });
    const at = localToIso(occurredAt);
    if (built.payload === null || at === null) return;
    send({ occurredAt: at, visibility, payload: built.payload });
  }

  const audience = audienceLabel(visibility);
  const frozen = unresolved !== null;

  return (
    <section className="panel panel--steward" aria-labelledby="composer-title">
      <h2 id="composer-title" className="panel__title">
        Draft a project update
      </h2>
      <p className="panel__lede">
        Say what changed or was tried, any artifact, the unresolved problem and the next step. Previewing sends nothing; only “Record this
        project update” records it, for the audience you choose.
      </p>
      {recorded && (
        <p className="form-success" role="status">
          Update recorded. It now appears in the history above.
        </p>
      )}

      {frozen && (
        <div className="notice notice--flag" role="alert" aria-label="Unclear recording">
          <p>
            No clear answer came back ({unresolved.message}). This update may already be recorded, so it is held unchanged until you check.
          </p>
          <div className="manifest" aria-label="Update awaiting reconciliation">
            <p className="minor-title">Sent, outcome unknown</p>
            <p>
              <strong>{stageLabel(unresolved.payload.stage)}</strong> · for {formatDateTime(unresolved.occurredAt)} · visible to{' '}
              {audienceLabel(unresolved.visibility)}
            </p>
            <p className="annotation__body">{unresolved.payload.note}</p>
            {unresolved.payload.artifactUrl !== undefined && <p className="small">Artifact: {unresolved.payload.artifactUrl}</p>}
          </div>
          {refresh === 'refreshing' && (
            <p role="status" className="small">
              Refreshing the history…
            </p>
          )}
          {refresh === 'failed' && (
            <p className="small">
              The history could not be refreshed, so it can’t be checked yet.{' '}
              <button type="button" className="button button--link" onClick={() => void refreshHistory(unresolved.failedAt)}>
                Refresh the history again
              </button>
            </p>
          )}
          {refresh === 'refreshed' && (
            <>
              <p className="small">The history above has been refreshed since this request. Check it for this update.</p>
              <div className="button-row">
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => {
                    setUnresolved(null);
                    setChecked(false);
                    resetDraft();
                  }}
                >
                  It’s in the history — close this
                </button>
              </div>
              <label className="checkbox">
                <input type="checkbox" checked={checked} onChange={(event) => setChecked(event.target.checked)} />
                <span>I checked the refreshed history and this update is not there</span>
              </label>
            </>
          )}
          <div className="button-row">
            <button
              type="button"
              className="button button--primary"
              disabled={record.isPending || refresh !== 'refreshed' || !checked}
              onClick={() => send({ occurredAt: unresolved.occurredAt, visibility: unresolved.visibility, payload: unresolved.payload })}
            >
              {record.isPending ? 'Recording…' : 'Record the same update again'}
            </button>
          </div>
        </div>
      )}

      {!frozen && !previewing && (
        <form className="form" onSubmit={preview} noValidate>
          <Field label="Stage" help="“Not known” is a valid answer. “Shipped” is a reported stage, not a verified deployment.">
            {(props) => (
              <select {...props} className="input" value={stage} onChange={(event) => setStage(event.target.value as ProjectStage)}>
                {stages.map((option) => (
                  <option key={option} value={option}>
                    {option === 'unknown' ? 'Not known' : option}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Update" error={errors.note ?? null}>
            {(props) => <textarea {...props} className="input textarea" rows={5} value={note} onChange={(event) => setNote(event.target.value)} />}
          </Field>
          <Field label="Artifact link (optional)" error={errors.artifactUrl ?? null} help="A reference link, not a pinned source version.">
            {(props) => <input {...props} className="input" type="url" value={artifactUrl} onChange={(event) => setArtifactUrl(event.target.value)} />}
          </Field>
          <Field label="When it happened (UTC)" error={errors.occurredAt ?? null}>
            {(props) => <input {...props} className="input" type="datetime-local" value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} />}
          </Field>
          <Field label="Visible to">
            {(props) => (
              <select {...props} className="input" value={visibility} onChange={(event) => setVisibility(event.target.value as Visibility)}>
                <option value="members">Invited members</option>
                <option value="stewards">Stewards</option>
                <option value="owner">Owner only</option>
              </select>
            )}
          </Field>
          {refused !== null && (
            <p className="form-error" role="alert">
              Not recorded ({refused.code}): {refused.message}
            </p>
          )}
          <button type="submit" className="button button--primary">
            Preview update
          </button>
        </form>
      )}

      {!frozen && previewing && (
        <div className="form">
          <div className="manifest" aria-label="Update preview">
            <p className="minor-title">Preview — not recorded yet</p>
            <p>
              <strong>{stageLabel(stage)}</strong> · for {formatDateTime(localToIso(occurredAt))} · visible to {audience}
            </p>
            <p className="annotation__body">{note.trim()}</p>
            {artifactUrl.trim() !== '' && <p className="small">Artifact: {artifactUrl.trim()}</p>}
          </div>
          {refused !== null && (
            <p className="form-error" role="alert">
              Not recorded ({refused.code}): {refused.message}
            </p>
          )}
          <div className="button-row">
            <button type="button" className="button button--primary" disabled={record.isPending} onClick={recordDraft}>
              {record.isPending ? 'Recording…' : `Record this project update for ${audience}`}
            </button>
            <button type="button" className="button button--secondary" disabled={record.isPending} onClick={() => setPreviewing(false)}>
              Edit
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function ProjectPage() {
  const { id = '' } = useParams();
  const detail = useProject(id);
  const pilot = PILOTS.find((candidate) => candidate.id === id);
  const project = detail.data;
  const notFound = detail.error instanceof ApiError && detail.error.isNotFound;
  const repository = project?.pilot?.repositoryUrl ?? pilot?.repository?.href;
  const selected = project?.pilot?.selected === true || pilot !== undefined;
  const title = project?.title ?? (notFound ? pilot?.title : undefined);

  return (
    <main id="main" tabIndex={-1} className="page">
      {detail.isPending && <Loading label="Loading the project" />}
      {detail.isError && !notFound && <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />}
      {notFound && pilot === undefined && (
        <EmptyState title="Project not found">
          <p>It does not exist, or it is not visible to your account.</p>
        </EmptyState>
      )}
      {title !== undefined && (
        <>
          <nav aria-label="Breadcrumb" className="breadcrumb">
            <ol>
              <li>
                <Link to="/pilots">Selected pilots</Link>
              </li>
            </ol>
          </nav>
          <header className="page__header">
            <p className="eyebrow">{selected ? 'Selected pilot · project' : 'Project'}</p>
            <h1 className="page__title">{title}</h1>
            {pilot !== undefined && <p className="tag tag--class pilot__status">{pilot.status}</p>}
            <p className="lede">{project?.summary ?? pilot?.goal}</p>
            <p className="link-list">
              {project?.canManage === true && (
                <Link to={`/projects/${encodeURIComponent(project.id)}/setup`} className="text-link">
                  Project setup →
                </Link>
              )}
              <Link to="/projects/new" className="text-link">
                Start a new project →
              </Link>
            </p>
            {repository !== undefined && isSafeExternalUrl(repository) && (
              <p>
                {/* Selecting a repository says nothing about its visibility. */}
                <a href={repository} target="_blank" rel="noreferrer noopener">
                  Repository<span className="visually-hidden"> (opens in a new tab)</span>
                </a>
              </p>
            )}
          </header>
          {project === undefined ? (
            <p className="notice notice--flag" role="note">
              This pilot isn’t registered as a hub project for your account yet, or isn’t visible to you, so there is no recorded history or
              update form here.
            </p>
          ) : (
            <>
              <section className="finding__section" aria-labelledby="updates-title">
                <h2 id="updates-title" className="section-title">
                  Recorded updates
                </h2>
                <p className="section-lede">Dates are when each update says it happened. A reported stage is not a verified outcome.</p>
                <History project={project} />
                <DiscordDelivery key={project.id} projectId={project.id} />
              </section>
              {/* The service decides who manages a project (canManage); the page never infers it from a role. */}
              {(project.canManage === true || selected) && (
                <section className="finding__section" aria-label="Record an update">
                  {project.canManage === true ? (
                    <Composer project={project} />
                  ) : (
                    <p className="muted">Updates for selected pilots are recorded by the project’s owner.</p>
                  )}
                </section>
              )}
            </>
          )}
        </>
      )}
    </main>
  );
}
